import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  deleteBotKnowledge,
  getBotProfile,
  listBotKnowledge,
  listBotLibraries,
  putBotProfile,
  saveBotKnowledge,
} from '../../api/endpoints';
import type { BotKnowledgeEntry, BotLibrary } from '../../api/types';
import { Badge } from '../../components/Badge';
import { BotAudiences } from './BotAudiences';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { Spinner } from '../../components/Spinner';
import { Toggle } from '../../components/Toggle';
import { useLoad } from '../../lib/useLoad';

/**
 * What the assistant knows beyond its business context: answers the company has
 * written, in its own words.
 *
 * Every enabled entry goes into the system prompt verbatim — there is no
 * retrieval step and no similarity search — which is why the budget bar is not
 * decoration. An entry past the ceiling is NOT in the prompt, and an operator
 * who is not told that has an assistant confidently ignorant of something they
 * are certain they wrote down.
 */
export function BotKnowledge() {
  return (
    <>
      <BotLibraries />
      <BotOwnAnswers />
      {/* Last, because it is the exception: everything above is what the
          assistant tells customers, which is every install until someone puts
          the widget on a page for their own chauffeurs. */}
      <BotAudiences />
    </>
  );
}

/**
 * Libraries are answers WE wrote — how the booking website and the customer
 * account pages work — that the assistant reads out as its own. They are
 * searched on demand rather than pasted into the prompt, which is why they cost
 * nothing against the budget bar below and why there is no size to manage here.
 *
 * The card sits ABOVE the tenant's own entries deliberately: a library is the
 * cheap way to cover the questions every operator gets, and the entries below
 * are for the answers only this company can give.
 */
function BotLibraries() {
  const libraries = useLoad(listBotLibraries);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entries = libraries.data?.payload ?? [];
  const available = libraries.data?.available ?? true;

  const toggle = async (library: BotLibrary, next: boolean) => {
    setBusy(library.id);
    setError(null);
    try {
      // Read-modify-write on the whole list, because flags are merged
      // server-side per key: sending only the delta would drop the others.
      const profile = await getBotProfile();
      const current = profile?.flags?.knowledgeLibraries ?? [];
      const knowledgeLibraries = next
        ? [...new Set([...current, library.id])]
        : current.filter((id) => id !== library.id);
      await putBotProfile({ flags: { ...(profile?.flags ?? {}), knowledgeLibraries } });
      libraries.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that.');
    } finally {
      setBusy(null);
    }
  };

  if (libraries.loading) return null;
  // Nothing on offer is not worth a card; unreachable is, because the operator
  // may have switched one on and needs to know it is not answering today.
  if (!available) {
    return (
      <section className="card card-pad manage-card">
        <h2 className="manage-card-title">Ready-made answers</h2>
        <Note tone="warn">
          These could not be loaded just now. Any you switched on are not being used until this
          clears — the assistant still answers from your own entries below.
        </Note>
      </section>
    );
  }
  if (!entries.length) return null;

  return (
    <section className="card card-pad manage-card">
      <div className="manage-card-head">
        <div>
          <h2 className="manage-card-title">Ready-made answers</h2>
          <p className="manage-card-sub">
            Written for you, about the reservation and account pages on your own website. Switch one on
            and the assistant looks things up in it when a customer asks — in its own words, under
            your name, without ever mentioning where the answer came from.
          </p>
        </div>
      </div>

      {error && <ErrorNote error={error} />}

      <ul className="bot-library-list">
        {entries.map((library) => (
          <li key={library.id}>
            <div className="bot-library-text">
              <span className="bot-library-name">{library.name}</span>
              <span className="bot-library-sub">{library.tagline}</span>
              <a className="bot-library-link" href="/help/docs" target="_blank" rel="noreferrer">
                Read the {library.articleCount} answers your customers would get
              </a>
            </div>
            <Toggle
              checked={library.enabled}
              disabled={busy === library.id}
              onChange={(next) => void toggle(library, next)}
              title={library.enabled ? 'On' : 'Off'}
            />
          </li>
        ))}
      </ul>

      <Note tone="info">
        Your own entries below always win. Where you have answered something yourself, the
        assistant uses your wording, not ours.
      </Note>
    </section>
  );
}

function BotOwnAnswers() {
  const knowledge = useLoad(listBotKnowledge);
  const [editing, setEditing] = useState<BotKnowledgeEntry | 'new' | null>(null);
  const [removing, setRemoving] = useState<BotKnowledgeEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const entries = knowledge.data?.payload ?? [];
  const usage = knowledge.data?.usage;
  const dropped = new Set(usage?.droppedIds ?? []);
  const pct = usage ? Math.min(100, Math.round((usage.used / usage.budget) * 100)) : 0;

  const persist = async (body: Parameters<typeof saveBotKnowledge>[0], id?: number) => {
    setBusy(true);
    setError(null);
    try {
      await saveBotKnowledge(body, id);
      setEditing(null);
      knowledge.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the entry.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry: BotKnowledgeEntry) => {
    setBusy(true);
    setError(null);
    try {
      await deleteBotKnowledge(entry.id);
      setRemoving(null);
      knowledge.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the entry.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card card-pad manage-card">
      <div className="manage-card-head">
        <div>
          <h2 className="manage-card-title">Your own answers</h2>
          <p className="manage-card-sub">
            Answers in your words. The assistant prefers these over its own wording and treats them
            as more current than anything else it knows.
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => setEditing('new')}>
          <Plus size={14} />
          Add
        </button>
      </div>

      {error && <ErrorNote error={error} />}

      {usage && (
        <div className="bot-budget">
          <div className="bot-budget-bar">
            <span style={{ width: `${pct}%` }} data-full={pct >= 100 || dropped.size > 0} />
          </div>
          <p className="manage-card-sub">
            {usage.kept} of {entries.filter((e) => e.enabled).length} active {'entries'} in the
            prompt — {pct}% of the space used.
            {dropped.size > 0 && (
              <>
                {' '}
                <strong>
                  {dropped.size} did not fit and {dropped.size === 1 ? 'is' : 'are'} not being used.
                </strong>{' '}
                Shorten or switch off something above it.
              </>
            )}
          </p>
        </div>
      )}

      {editing && (
        <KnowledgeForm
          entry={editing === 'new' ? null : editing}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSave={(body) => void persist(body, editing === 'new' ? undefined : editing.id)}
        />
      )}

      {knowledge.loading ? (
        <Spinner />
      ) : knowledge.error ? (
        <ErrorNote error={knowledge.error} />
      ) : entries.length === 0 ? (
        <Note tone="info">
          Nothing yet. Add the questions your team answers over and over — the ones where a wrong
          answer costs you a reservation.
        </Note>
      ) : (
        <ul className="bot-knowledge-list">
          {entries.map((entry) => (
            <li key={entry.id} className={dropped.has(entry.id) ? 'is-dropped' : ''}>
              <div className="bot-knowledge-text">
                <span className="bot-knowledge-q">
                  {entry.kind === 'qa' ? entry.question : 'Note'}
                </span>
                <span className="bot-knowledge-a">{entry.answer}</span>
              </div>
              <div className="bot-knowledge-actions">
                {!entry.enabled && <Badge tone="neutral">Off</Badge>}
                {dropped.has(entry.id) && <Badge tone="warn">Not in prompt</Badge>}
                <button
                  className="btn btn-ghost btn-icon"
                  aria-label="Edit"
                  onClick={() => setEditing(entry)}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="btn btn-ghost btn-icon"
                  aria-label="Delete"
                  onClick={() => setRemoving(entry)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {removing && (
        <ConfirmDialog
          title="Delete this entry?"
          body="The assistant stops using it on its next reply."
          confirmLabel="Delete"
          danger
          busy={busy}
          onCancel={() => setRemoving(null)}
          onConfirm={() => void remove(removing)}
        />
      )}
    </section>
  );
}

function KnowledgeForm({
  entry,
  busy,
  onCancel,
  onSave,
}: {
  entry: BotKnowledgeEntry | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (body: { kind: 'qa' | 'note'; question: string | null; answer: string; enabled: boolean }) => void;
}) {
  const [kind, setKind] = useState<'qa' | 'note'>(entry?.kind ?? 'qa');
  const [question, setQuestion] = useState(entry?.question ?? '');
  const [answer, setAnswer] = useState(entry?.answer ?? '');
  const [enabled, setEnabled] = useState(entry?.enabled ?? true);

  return (
    <div className="bot-knowledge-form">
      <div className="bot-kind-switch">
        <button
          type="button"
          className={kind === 'qa' ? 'is-on' : ''}
          onClick={() => setKind('qa')}
        >
          Question &amp; answer
        </button>
        <button
          type="button"
          className={kind === 'note' ? 'is-on' : ''}
          onClick={() => setKind('note')}
        >
          Note
        </button>
      </div>
      {kind === 'qa' && (
        <Field label="Question" hint="How a customer would ask it.">
          <input
            className="input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Do you charge for waiting time at the airport?"
          />
        </Field>
      )}
      <Field
        label={kind === 'qa' ? 'Answer' : 'What it should know'}
        hint={kind === 'qa' ? 'Word it the way you want the customer to hear it.' : 'A fact or policy, in a sentence or two.'}
      >
        <textarea className="input" rows={4} value={answer} onChange={(e) => setAnswer(e.target.value)} />
      </Field>
      <Toggle
        checked={enabled}
        onChange={setEnabled}
        title="Active"
        sub="Switch off to keep it without the assistant using it."
      />
      <div className="manage-card-actions manage-card-actions-split">
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={busy || !answer.trim() || (kind === 'qa' && !question.trim())}
          onClick={() => onSave({ kind, question: kind === 'qa' ? question : null, answer, enabled })}
        >
          {busy && <Spinner />}
          {entry ? 'Save entry' : 'Add entry'}
        </button>
      </div>
    </div>
  );
}
